import axios from "axios";
import { getUserToken } from "@/utils/access-provider";

const HEADERS_CROWLINGO = {
  "Content-Type": "application/json",
};

// Use Vite environment variable, fallback to process.env for SSR compatibility
export const API_BASE_URL =
  typeof import.meta !== "undefined" && import.meta.env?.VITE_API_CROWLINGO
    ? import.meta.env.VITE_API_CROWLINGO
    : typeof process !== "undefined"
      ? process.env.API_CROWLINGO
      : "http://127.0.0.1:8000/";

export const API_CROWLINGO = axios.create({
  baseURL: API_BASE_URL,
  headers: HEADERS_CROWLINGO,
});

/**
 * Check if a workspace is selected in localStorage.
 * Used to conditionally enable queries that require workspace context.
 */
export const hasSelectedWorkspace = (): boolean => {
  try {
    const stored = localStorage.getItem("selectedWorkspaceId");
    if (!stored) return false;
    const parsed = JSON.parse(stored);
    return !!parsed;
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// Workspace ID external store for useSyncExternalStore.
// Provides a single shared subscription so every useApiQuery consumer can
// include workspace ID in its query key without per-hook event listeners.
// ---------------------------------------------------------------------------

const WORKSPACE_KEY = "selectedWorkspaceId";

/** Synchronous snapshot — reads localStorage directly. */
export const getWorkspaceId = (): string | null => {
  try {
    const stored = localStorage.getItem(WORKSPACE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) ?? null;
  } catch {
    return null;
  }
};

/** SSR snapshot (always null). */
export const getServerWorkspaceId = (): string | null => null;

/**
 * Subscribe to workspace ID changes.
 * Listens for the "local-storage-change" custom event dispatched by
 * useLocalStorage and the native "storage" event for cross-tab sync.
 */
export const subscribeWorkspaceId = (callback: () => void): (() => void) => {
  const handleCustom = (e: Event) => {
    if ((e as CustomEvent).detail?.key === WORKSPACE_KEY) callback();
  };
  const handleStorage = (e: StorageEvent) => {
    if (e.key === WORKSPACE_KEY) callback();
  };
  window.addEventListener("local-storage-change", handleCustom);
  window.addEventListener("storage", handleStorage);
  return () => {
    window.removeEventListener("local-storage-change", handleCustom);
    window.removeEventListener("storage", handleStorage);
  };
};

// Inject X-Workspace-Id and X-Branch-Id headers for all requests when available.
// The backend ignores these headers for endpoints that don't need them.
API_CROWLINGO.interceptors.request.use((config) => {
  const stored = localStorage.getItem("selectedWorkspaceId");
  if (stored) {
    try {
      const workspaceId = JSON.parse(stored);
      if (workspaceId) {
        config.headers!["X-Workspace-Id"] = workspaceId;
      }
    } catch {
      // Ignore JSON parse errors
    }
  }

  // Inject X-Branch-Id header from branch store
  // null = main branch (no header sent, backend defaults to main)
  const branchStoreState = localStorage.getItem("branch-store");
  if (branchStoreState) {
    try {
      const parsed = JSON.parse(branchStoreState);
      const branchId = parsed?.state?.activeBranchId;
      if (branchId) {
        config.headers!["X-Branch-Id"] = branchId;
      }
    } catch {
      // Ignore JSON parse errors
    }
  }

  return config;
});

export const API_CROWLINGO_AUTHED = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "X-API-KEY": "",
    ...HEADERS_CROWLINGO,
  },
});

// Inject auth token and context headers for all requests when available.
// The backend ignores these headers for endpoints that don't need them.
API_CROWLINGO_AUTHED.interceptors.request.use((config) => {
  config.headers!["X-API-KEY"] = getUserToken();

  // Inject X-Workspace-Id header
  const storedWorkspace = localStorage.getItem("selectedWorkspaceId");
  if (storedWorkspace) {
    try {
      const workspaceId = JSON.parse(storedWorkspace);
      if (workspaceId) {
        config.headers!["X-Workspace-Id"] = workspaceId;
      }
    } catch {
      // Ignore JSON parse errors
    }
  }

  // Inject X-Branch-Id header from branch store
  // null = main branch (no header sent, backend defaults to main)
  const branchStoreState = localStorage.getItem("branch-store");
  if (branchStoreState) {
    try {
      const parsed = JSON.parse(branchStoreState);
      const branchId = parsed?.state?.activeBranchId;
      if (branchId) {
        config.headers!["X-Branch-Id"] = branchId;
      }
    } catch {
      // Ignore JSON parse errors
    }
  }

  return config;
});

// export const getApiSchema = () => API_CROWLINGO_AUTHED.get<SchemaModel>("/monitoring-schema")
